import { useFormik } from 'formik';
import { useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row
} from 'reactstrap';
import { InputField } from 'src/components';
import { useGetChangeRequestApprovalMutation } from 'src/rtk/features/changeRequest/changeRequest';
import { useCreateTitleApprovalForRtmdMutation } from 'src/rtk/features/courseApproval/courseApprovalApi';
import * as Yup from 'yup';

interface Props {
  isOpen: boolean;
  toggle: any;
  info: any;
}

const initialValues = {
  admin_approval_status: '',
  note: '',
};

const ChangeRequestsApproval = (props: Props) => {
  const { isOpen, toggle, info } = props;
  // console.log(info);
  const buttonRef = useRef<any>();

  const [getChangeRequestApproval, { isSuccess, isError, error }] = useGetChangeRequestApprovalMutation();

  const err = error as CustomError;

  const {
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    handleChange,
    getFieldProps,
    touched,
    errors,
  } = useFormik({
    initialValues,
    validationSchema: Yup.object({
      admin_approval_status: Yup.string().required('Please Choose Status!'),
      note: Yup.string().when('admin_approval_status', (admin_approval_status, schema) => {
        if (admin_approval_status === 'Correction') {
          return schema.required('Correction note is Required!');
        }
        return schema;
      }),
    }),
    onSubmit: (values) => {
      buttonRef.current.disabled = true;
      const url = `${info?.id}/${values.admin_approval_status}?note=${values.note}`;
      getChangeRequestApproval(url);
    },
  });


  useEffect(() => {
    if (isSuccess) {
      toast.success('Approval Status Updated Successfully!');
      buttonRef.current.disabled = false;
    }
    if (isError) {
      toast.error('Something went wrong');
      buttonRef.current.disabled = false;
    }
  }, [isSuccess, isError]);

  return (
    <Modal
      isOpen={isOpen}
      backdrop={'static'}
      toggle={toggle}
      size="lg"
    >
      <ModalHeader toggle={toggle}>Change Request Approval</ModalHeader>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(e);
        }}
      >
        <ModalBody>
          <div>
            <h6 className="block-heading">Change Details</h6>
            <div className="row">
              <div className='col-md-12'>
                <table className="table table-bordered table-striped table-sm">
                  <tbody>
                    <tr>
                      <td colSpan={2}><strong>Trainee Name: </strong>{info?.trainee_name}</td>
                    </tr>
                    <tr>
                      <td className="col-md-6 col-lg-6">
                        <strong>Type: </strong>
                        {info?.type}
                      </td>
                      <td className="col-md-6 col-lg-6">
                        <strong>Collumn Name: </strong>
                        {info?.column_name === 'title_of_thesis' ? 'Title of the Thesis'
                          : info?.column_name === 'place_of_study' ? 'Place of Study'
                            : info?.column_name === 'study_period' ? 'Study Period'
                              : info?.column_name === 'guide_id' ? 'Guide'
                                : info?.column_name === 'co_guide_id' ? 'Co-Guide'
                                  : ''
                        }
                      </td>
                    </tr>
                    <tr>
                      <td>
                        <strong>Previous Value: </strong>
                        {info?.previous_value_info ?
                          info?.previous_value_info?.map((item: any) => (
                            <div key={item.registration_no}>
                              {item?.name}
                            </div>
                          )) : info?.previous_value ?? 'N/A'}
                      </td>
                      <td>
                        <strong>Updated Value: </strong>
                        {info?.updated_value_info ?
                          info?.updated_value_info?.map((item: any) => (
                            <div key={item.registration_no}>
                              {item?.name}
                            </div>
                          )) : info?.updated_value ?? 'N/A'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <h6 className="block-heading">Approval Status</h6>
          <Row>
            {info?.admin_approval_status !== 'Approve' && info?.admin_approval_status !== 'Reject' ?
              <>
                <div className="col">
                  <FormGroup check inline className="mb-3 ms-2">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="profile_approval_status"
                      id="profile-approve"
                      checked={values.admin_approval_status === 'Approve'}
                      onChange={(e) => {
                        setFieldValue('admin_approval_status', 'Approve');
                      }}
                    />
                    <label
                      className="form-check-label text-success"
                      htmlFor="profile-approve"
                    >
                      Accept
                    </label>
                  </FormGroup>
                  <FormGroup check inline className="mb-3 ">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="profile_approval_status"
                      checked={values.admin_approval_status === 'Reject'}
                      id="reject"
                      onChange={() => {
                        setFieldValue('admin_approval_status', 'Reject');
                      }}
                    />
                    <label
                      className="form-check-label text-danger"
                      htmlFor="reject"
                    >
                      Reject
                    </label>
                  </FormGroup>

                  {errors.admin_approval_status && touched.admin_approval_status ? (
                    <div className="text-danger error-size">{errors.admin_approval_status}</div>
                  ) : null}
                </div>

                <div className="col">
                  <Label for="note">{values.admin_approval_status}&nbsp;Note</Label>
                  <InputField
                    type="textarea"
                    placeholder="Enter.."
                    invalid={{ errors, touched }}
                    {...getFieldProps('note')}
                  />
                </div>
              </>
              :
              <div className='d-flex gap-1 justify-content-start'>
                <strong>Admin Approval Status : </strong>
                <div>
                  {info?.admin_approval_status?.toLowerCase() === 'approve' && (
                    <div className="badge rounded-pill d-inline-block badge-approved font-size-12 fw-medium">
                      {'Accepted'}
                    </div>
                  )}
                  {info?.admin_approval_status?.toLowerCase() === 'reject' && (
                    <div className="badge rounded-pill d-inline-block badge-rejected font-size-12 fw-medium">
                      {info?.admin_approval_status}
                    </div>
                  )}
                  {info?.admin_approval_status?.toLowerCase() === 'correction' && (
                    <div className="badge rounded-pill d-inline-block badge-correction font-size-12 fw-medium">
                      {info?.admin_approval_status}
                    </div>
                  )}
                  {info?.admin_approval_status !== 'Approve' &&
                    info?.admin_approval_status !== 'Reject' &&
                    info?.admin_approval_status !== 'Correction' && (
                      <div className="badge rounded-pill d-inline-block badge-pending font-size-12 fw-medium">
                        {info?.admin_approval_status ?? 'Pending'}
                      </div>
                    )}
                </div>
              </div>
            }

          </Row>
        </ModalBody>

        <ModalFooter>
          <div className="d-flex gap-2 ">
            {info?.admin_approval_status !== 'Approve' && info?.admin_approval_status !== 'Reject' ?
              <button
                type="submit"
                className="btn btn-primary"
                ref={buttonRef}
              >
                Save
              </button>
              : null
            }
            <Button color="danger" onClick={toggle}>
              Close
            </Button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default ChangeRequestsApproval;
